import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import { delay, distinctUntilChanged, filter, map, startWith, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { ButtonPosition, NotificationTheme } from '@fcom/ui-components';
import { GtmService } from '@fcom/common/gtm';
import {
  GaContext,
  GtmEcommerceMeasurement,
  GtmEvent,
  GtmPurchaseFlow,
  GtmTripType,
  LoaderType,
  MultivariateTestId,
  TestVariant,
  ToasterMessage,
} from '@fcom/common/interfaces';
import { ConfigService } from '@fcom/core/services';
import {
  Category,
  FinnairBoundItem,
  FinnairCart,
  FinnairItineraryItemFlight,
  FinnairPassengerItem,
  FinnairPrice,
  FinnairServiceCatalogCampaign,
  FinnairServiceItem,
  PaxLevelQuota,
} from '@fcom/dapi/api/models';
import {
  Amount,
  CartPartStatus,
  CartStatus,
  LocationInfoMap,
  ServiceAvailability,
  ServiceCatalogCategory,
  ServiceCatalogCategoryServicesMap,
  ServiceCatalogSelectionsForAllTravelers,
  ServiceCatalogServices,
} from '@fcom/dapi/interfaces';
import { finShare, snapshot } from '@fcom/rx';
import { compareAsJson, isNotEmpty, isPresent, unique, unsubscribe } from '@fcom/core/utils';
import {
  ancillarySaleBlockingSupportedReasonKeys,
  BOUND_BASED_CATEGORIES,
  buttonTexts,
  categoryHasQuotaLeft,
  changedServices,
  filterCategories,
  getCountForCategoryStream,
  getLowestPriceForCategory,
  getPriceForCategoryStream,
  getProductTag,
  getQuantitiesForFragmentsAndPassenger,
  isAllowedToSell,
  isBoundBasedCategory,
  isJourneyBasedCategory,
  PETC_CONFIRM_MODAL_TRANSLATION_KEYS,
} from '@fcom/common-booking/modules/ancillaries/utils';
import {
  ButtonState,
  ConfirmModal,
  ConfirmModalData,
  EligibilityWithCategory,
  SeatChangeEvent,
} from '@fcom/common-booking/modules/ancillaries/interfaces';
import {
  AncillarySubviewComponent,
  ConfirmModalTranslationKeys,
  ProductCardNotification,
  SmpProduct,
} from '@fcom/common-booking/modules/ancillaries/components';
import {
  seatSelections,
  serviceCatalog,
  serviceCatalogCampaigns,
  serviceCategories,
  variantsQuantity,
} from '@fcom/common-booking/store/selectors';
import { QuantityForFragmentAndPassenger, SeatMapActions } from '@fcom/common-booking/store';
import { BookingSummaryService, FlightWithStreams } from '@fcom/common-booking';
import { ScrollService, Step } from '@fcom/common';
import { profileOrNot, profileType } from '@fcom/core/selectors';
import { ProfileTier, ProfileType } from '@fcom/core-api/login';
import { isBound, passengersMissingCategoryService, toAmount } from '@fcom/common-booking/utils/common-booking.utils';
import { getServiceData } from '@fcom/common-booking/utils/ancillaries.utils';
import { getTripTypeFromBounds } from '@fcom/common/utils/gtm.utils';
import { setNoAvailability } from '@fcom/common-booking/store/actions/seat-map.actions';
import { BookingAppState } from '@fcom/common/interfaces/booking';
import { AncillaryLoaderStatus } from '@fcom/common-booking/modules/ancillaries/enums';
import { MultivariateTestService } from '@fcom/common/multivariate-test/services/multivariate-test.service';
import { BOOKING_STEPS } from '@fcom/common/config/booking-config';

import { cartData, cartFlights, cartPartStatusForCategory, cartStatus, isCartDirty } from '../../../../store/selectors';
import { BookingQueueService, BookingService } from '../../../../services';
import { combinedCartServicesMap, currentServices } from '../../../../store/selectors/booking-cart-state.selector';

const SEAT_WIFI_BUNDLE_REMOVAL_CONFIRM_MODAL_TRANSLATION_KEYS = {
  title: 'services.wifi.bundleRemovalConfirmModal.title',
  firstContentParagraph: 'services.wifi.bundleRemovalConfirmModal.firstContentParagraph',
  secondContentParagraph: 'services.wifi.bundleRemovalConfirmModal.secondContentParagraph',
  closeButton: 'services.wifi.bundleRemovalConfirmModal.closeButton',
  continueButton: 'services.wifi.bundleRemovalConfirmModal.continueButton',
};
@Component({
  selector: 'fin-smp',
  templateUrl: 'smp.component.html',
  styleUrls: ['./smp.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmpComponent implements OnInit, OnDestroy {
  readonly Category = Category;
  readonly LoaderType = LoaderType;
  readonly ButtonPosition = ButtonPosition;
  readonly buttonText = buttonTexts;
  readonly NotificationTheme = NotificationTheme;
  readonly BOUND_BASED_CATEGORIES = BOUND_BASED_CATEGORIES;
  readonly PETC_CONFIRM_MODAL_TRANSLATION_KEYS = PETC_CONFIRM_MODAL_TRANSLATION_KEYS;
  readonly SEAT_WIFI_BUNDLE_REMOVAL_CONFIRM_MODAL_TRANSLATION_KEYS =
    SEAT_WIFI_BUNDLE_REMOVAL_CONFIRM_MODAL_TRANSLATION_KEYS;
  readonly TRACKING_CONTEXT = GaContext.BOOKING_FLOW;
  readonly ProfileType = ProfileType;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  @Input() cartDataReady$: Observable<boolean>;
  @Input() priceUpdating$: Observable<boolean>;
  @Input() hasTravelerInCart$: Observable<boolean>;
  @Input() flights$: Observable<FlightWithStreams[]>;
  @Input() bounds$: Observable<FinnairBoundItem[]>;
  @Input() travelers$: Observable<FinnairPassengerItem[]>;
  @Input() servicesMap$: Observable<ServiceCatalogCategoryServicesMap>;
  @Input() cartTotalPrice$: Observable<FinnairPrice>;
  @Input() servicesReady$: Observable<boolean>;
  @Input() availability$: Observable<ServiceAvailability>;
  @Input() locations$: Observable<LocationInfoMap>;
  @Input() passengersWithPetcIncompatibleSeats$: Observable<{ [boundId: string]: string[] }>;
  @Input() ancillaryLoader$: BehaviorSubject<AncillaryLoaderStatus> = new BehaviorSubject(AncillaryLoaderStatus.OFF);

  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() message: EventEmitter<ToasterMessage> = new EventEmitter();
  @Output() changeStep: EventEmitter<Step> = new EventEmitter();
  @Output() continueToNextPhase: EventEmitter<void> = new EventEmitter();

  currentServices$: Observable<ServiceCatalogServices>;
  boundsOrFlights$: Observable<(FinnairBoundItem | FinnairItineraryItemFlight)[]>;
  boundOrFlightAncillaryAvailability$: Observable<{ [fragmentId: string]: EligibilityWithCategory[] }>;
  activeFlight: FinnairBoundItem | FinnairItineraryItemFlight;
  sideBarOpen = false;
  subscriptions: Subscription = new Subscription();
  showCtaButton$: Observable<boolean>;
  serviceCategories$: Observable<ServiceCatalogCategory[]>;
  products$: Observable<SmpProduct[]>;
  cart$: Observable<FinnairCart>;
  cartFlights$: Observable<FinnairItineraryItemFlight[]>;
  currentStep$: Observable<Step>;
  unpaidServices$: Observable<BookingSummaryService[]>;
  combinedServices$: Observable<{ [category: string]: FinnairServiceItem }>;
  totalPrice$: Observable<Amount>;
  totalPoints$: Observable<number>;
  profileType$: Observable<ProfileType>;
  seatSelectionLoading$ = new BehaviorSubject<boolean>(false);
  passengersWithSeatWifiBundleSelected$: Observable<{ [flightId: string]: string[] }>;

  currentCombinedServices$: Observable<FinnairServiceItem>;
  tripType$: Observable<GtmTripType>;
  purchaseFlow$: Observable<GtmPurchaseFlow>;
  confirmModalData$ = new BehaviorSubject<ConfirmModal | undefined>({
    open: false,
  });
  serviceQuantityMap$: Observable<QuantityForFragmentAndPassenger>;
  paxLevelQuota$: Observable<PaxLevelQuota>;
  showSeatPreSelection$: Observable<boolean>;
  seatSelections$: Observable<ServiceCatalogSelectionsForAllTravelers>;

  @ViewChild('ancillarySubview', { static: false })
  ancillarySubview: AncillarySubviewComponent;

  selectedCategory$: BehaviorSubject<Category> = new BehaviorSubject<Category>(undefined);
  ancillarySubviewOpen$: Subject<boolean> = new BehaviorSubject(false);
  enableBookingNewProductCardLayout$: Observable<boolean>;
  enableSpecialUpsellCategory$: Observable<boolean>;
  enableShowCheaperOnlineTag = false;
  specialUpsellCategory$: Observable<Category>;

  private _showCtaButtonSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private _seatPreselectionDismissed$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private configService: ConfigService,
    private store$: Store<BookingAppState>,
    private bookingQueueService: BookingQueueService,
    private gtmService: GtmService,
    private bookingService: BookingService,
    private scrollService: ScrollService,
    private multivariateTestService: MultivariateTestService
  ) {}

  ngOnInit(): void {
    this.enableBookingNewProductCardLayout$ = this.multivariateTestService
      .getTestVariant(MultivariateTestId.NEW_PRODUCT_CARDS)
      .pipe(
        map(({ variant }) => variant === TestVariant.B),
        finShare()
      );

    // TODO: Create own AB test for this.
    this.enableSpecialUpsellCategory$ = this.configService.cfg.enableSpecialUpsellCategory
      ? this.multivariateTestService.getTestVariant(MultivariateTestId.REGRESSION_TEST_WORKAROUND, false).pipe(
          map(({ variant }) => variant === TestVariant.B),
          finShare()
        )
      : of(false).pipe(finShare());
    this.enableShowCheaperOnlineTag = this.configService.cfg.enableShowCheaperOnlineTag;

    this.currentServices$ = this.selectedCategory$.pipe(
      switchMap((currentCategory) => this.servicesMap$.pipe(map((services) => services?.[currentCategory])))
    );

    this.purchaseFlow$ = this.store$.pipe(
      profileOrNot(),
      map((profile) => (profile?.tier === ProfileTier.CORPORATE ? GtmPurchaseFlow.CORPORATE : GtmPurchaseFlow.BOOKING))
    );
    this.serviceCategories$ = this.store$.pipe(serviceCategories());
    this.cart$ = this.store$.pipe(cartData());
    this.cartFlights$ = this.store$.pipe(cartFlights());
    this.unpaidServices$ = this.store$.pipe(currentServices());
    this.combinedServices$ = this.store$.pipe(combinedCartServicesMap(), finShare());
    this.currentCombinedServices$ = this.selectedCategory$.pipe(
      switchMap((currentCategory) =>
        this.combinedServices$.pipe(
          map((services) => services?.[currentCategory]),
          finShare()
        )
      )
    );
    this.currentStep$ = this.bookingService.step;
    this.tripType$ = this.bounds$.pipe(map((bounds) => getTripTypeFromBounds(bounds)));
    this.profileType$ = this.store$.pipe(profileType());
    this.showSeatPreSelection$ = combineLatest([this._seatPreselectionDismissed$, this.cart$]).pipe(
      map(([dismissed, cart]) => {
        return (
          this.bookingService.currentStep?.id === BOOKING_STEPS.SEATS.id &&
          !dismissed &&
          !this.bookingService.cartHasCorporateSeatBenefit(cart)
        );
      })
    );

    this.seatSelections$ = this.store$.pipe(seatSelections());

    this.subscriptions.add(
      this.showSeatPreSelection$.subscribe((isSeatSelection) => {
        if (isSeatSelection) {
          this.sideBarOpen = true;
        }
      })
    );

    this.subscriptions.add(
      this.showSeatPreSelection$.pipe(filter(Boolean)).subscribe(() => {
        this.selectedCategory$.next(Category.SEAT);
      })
    );

    this.paxLevelQuota$ = this.store$.pipe(serviceCatalog()).pipe(
      map((catalog) => catalog.paxLevelQuota),
      distinctUntilChanged(),
      finShare()
    );

    this.serviceQuantityMap$ = this.paxLevelQuota$.pipe(
      switchMap(({ variants }) => {
        return combineLatest([this.store$.pipe(variantsQuantity(variants)), this.store$.pipe(cartData())]).pipe(
          map(([selectedServices, cart]) => getQuantitiesForFragmentsAndPassenger(selectedServices, undefined, cart))
        );
      }),
      finShare()
    );

    const unpaidServices$ = this.store$.pipe(
      cartData(),
      map((cd: FinnairCart) => cd.services.unpaid)
    );

    const fallbackCurrencyCode$ = this.store$.pipe(
      cartData(),
      map((cd: FinnairCart) => cd.prices.unpaid.total.total.totalAmount.currencyCode)
    );

    const getStatusStream = (category: Category): Observable<ButtonState> => {
      return combineLatest([
        this.store$.pipe(cartPartStatusForCategory(category)),
        getCountForCategoryStream(category, unpaidServices$),
      ]).pipe(
        map(([status, numberOfItems]) => {
          if (status === CartPartStatus.UPDATING) {
            return ButtonState.UPDATING;
          } else if ((status === CartPartStatus.ERROR && numberOfItems < 1) || numberOfItems === 0) {
            return ButtonState.PLUS;
          } else {
            return ButtonState.OK;
          }
        }),
        finShare()
      );
    };

    this.specialUpsellCategory$ = combineLatest([this.enableSpecialUpsellCategory$, this.cart$]).pipe(
      map(([enableSpecialUpsellCategory, cart]) =>
        enableSpecialUpsellCategory ? this.getUpsellCategory(cart) : undefined
      ),
      startWith(undefined),
      finShare()
    );

    const campaignsCategories$ = this.store$.pipe(
      serviceCatalogCampaigns(),
      startWith([] as FinnairServiceCatalogCampaign[]),
      map((campaigns) =>
        campaigns
          .flatMap((c) => c.categories)
          .flatMap((c) => c.id)
          .filter(unique)
      ),
      finShare()
    );

    this.products$ = combineLatest([this.serviceCategories$, this.specialUpsellCategory$]).pipe(
      map(([categories, specialUpsellCategory]) => ({
        categories: filterCategories(categories),
        specialUpsellCategory,
      })),
      withLatestFrom(campaignsCategories$, this.combinedServices$, this.cart$),
      map(([{ categories, specialUpsellCategory }, campaignsCategories, combinedServices, cart]) => {
        return categories.map((serviceCatalogCategory): SmpProduct => {
          const category = serviceCatalogCategory.category;
          const noQuotaLeft = !categoryHasQuotaLeft(category, categories);
          const count$ = getCountForCategoryStream(category, unpaidServices$);
          const hasCorporateSeatBenefit =
            category === Category.SEAT && this.bookingService.cartHasCorporateSeatBenefit(cart);
          const lowestPrice = !hasCorporateSeatBenefit && getLowestPriceForCategory(serviceCatalogCategory);
          return {
            category: category,
            title: serviceCatalogCategory.translations.title,
            description: serviceCatalogCategory.translations.description,
            image: serviceCatalogCategory.media,
            status$: getStatusStream(category),
            price$: getPriceForCategoryStream(category, unpaidServices$, fallbackCurrencyCode$),
            count$: count$,
            noQuotaLeft: noQuotaLeft,
            notAvailable$: of(hasCorporateSeatBenefit),
            productCardNotification$: count$.pipe(
              map((count) =>
                this.resolveProductCardNotification(category, count, noQuotaLeft, hasCorporateSeatBenefit)
              ),
              finShare()
            ),
            lowestPrice: lowestPrice?.money,
            originalPrice: lowestPrice?.originalPrice,
            productTag: getProductTag(category, campaignsCategories, combinedServices?.[category], {
              showCheaperOnlineTag: this.enableShowCheaperOnlineTag,
              enableTierBenefitTag: this.configService.cfg.enableTravelExtrasTierBenefitTag,
            }),
            hideViewButton: category === Category.COVER || hasCorporateSeatBenefit,
            isSpecialUpsellProduct: category === specialUpsellCategory,
          };
        });
      }),
      map((products) =>
        products.some((product) => product.isSpecialUpsellProduct)
          ? products.sort((a, b) => Number(b.isSpecialUpsellProduct) - Number(a.isSpecialUpsellProduct))
          : products
      ),
      finShare()
    );

    this.showCtaButton$ = this._showCtaButtonSubject$.pipe(
      switchMap((show) => {
        return show ? of(show).pipe(delay(250)) : of(show);
      })
    );

    this.totalPrice$ = this.cartTotalPrice$.pipe(
      map((total) => {
        if (!total) {
          return;
        }

        return toAmount(total?.totalAmount);
      })
    );

    this.totalPoints$ = this.cartTotalPrice$.pipe(
      filter((total) => !!total?.totalPoints),
      map((total) => +total.totalPoints.amount)
    );

    this.boundsOrFlights$ = this.selectedCategory$.pipe(
      filter(Boolean),
      switchMap((category) => {
        if (isJourneyBasedCategory(category)) {
          return of([]);
        } else if (isBoundBasedCategory(category)) {
          return this.bounds$;
        } else {
          return this.flights$;
        }
      }),
      startWith([]),
      finShare()
    );

    this.boundOrFlightAncillaryAvailability$ = combineLatest([
      this.availability$,
      this.boundsOrFlights$,
      this.selectedCategory$,
      this.store$.pipe(cartData()),
    ]).pipe(
      map(([availability, boundsOrFlights, category, cart]) => {
        return boundsOrFlights.reduce((all, boundOrFlight) => {
          const allowedToSell = isAllowedToSell(category, cart, availability, boundOrFlight);
          all[boundOrFlight.id] = allowedToSell.map((eligibility) => {
            return {
              ...eligibility,
              isAllowedToUse:
                eligibility.isAllowedToUse ||
                Object.keys(ancillarySaleBlockingSupportedReasonKeys).includes(eligibility.reason),
            };
          });
          return all;
        }, {});
      }),
      distinctUntilChanged(compareAsJson),
      finShare()
    );

    this.passengersWithSeatWifiBundleSelected$ = combineLatest([this.travelers$, this.flights$]).pipe(
      map(([travelers, flights]) => {
        // TODO: this should also check that traveler has seat + wifi bundle selected
        return flights.reduce(
          (acc, flight) => {
            acc[flight.id] = this.configService.cfg.enableBookingFlowAncillaryBundles
              ? travelers.map((traveler) => traveler.id)
              : [];
            return acc;
          },
          {} as { [flightId: string]: string[] }
        );
      }),
      finShare()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  showSubview(category: Category, slideIndexToOpen: number): void {
    this.closeSidebar();
    this.ancillarySubviewOpen$.next(true);
    this.selectedCategory$.next(category);
    this._showCtaButtonSubject$.next(false);

    this.ancillarySubview.open(null, slideIndexToOpen);
  }

  viewDetails(category: Category): void {
    this.selectedCategory$.next(category);
    if (category !== Category.COVER) {
      this.sideBarOpen = true;
      this._showCtaButtonSubject$.next(false);
    }
  }

  updateCartServices(category: Category): void {
    this.selectedCategory$.next(null);
    this.ancillarySubviewOpen$.next(false);
    this._showCtaButtonSubject$.next(true);
    this.confirmModalData$.next({ open: false });

    if (snapshot(this.store$.pipe(isCartDirty()))) {
      if (category === Category.PET) {
        this.store$.dispatch(SeatMapActions.reset());
      }
      const bounds = snapshot(this.bounds$);
      const flights = snapshot(this.flights$);
      const cartDataSnapshot = snapshot(this.store$.pipe(cartData()));
      const purchaseFlow = snapshot(this.purchaseFlow$);

      const [requests, messages] = getServiceData(this.store$, category, bounds, flights);
      this.bookingQueueService.queueServicesUpdate(requests, {
        next: (cart) => {
          this.sendEcommerceAddRemoveEvents(category, cartDataSnapshot, cart, purchaseFlow);
        },
        error: () => {
          if (category === Category.SEAT) {
            this.store$.dispatch(SeatMapActions.reset());
            this.store$.dispatch(
              SeatMapActions.loadSeatSelectionsFromCart({ cartData: snapshot(this.store$.pipe(cartData())) })
            );
          }
          this.message.emit(messages.error);
        },
      });
    }
  }

  closeSeatPreselection(updateServices: boolean): void {
    if (this.bookingService.currentStep?.id === BOOKING_STEPS.SEATS.id) {
      this.bookingService.nextStep();
    }
    this._seatPreselectionDismissed$.next(true);
    this.selectedCategory$.next(null);
    this.scrollService.scrollTop();
    if (updateServices) {
      this.updateCartServices(Category.SEAT);
    }
  }

  loadNextSeatMap(flightOrBound: FinnairBoundItem | FinnairItineraryItemFlight): void {
    this.activeFlight = flightOrBound;
    if (isPresent(flightOrBound) && !isBound(flightOrBound) && this.selectedCategory$.value === Category.SEAT) {
      this.subscriptions.add(
        this.travelers$
          .pipe(filter(isNotEmpty))
          .pipe(withLatestFrom(this.store$.pipe(cartData(), filter(Boolean), take(1))))
          .subscribe(([, cart]: [FinnairPassengerItem[], FinnairCart]) => {
            const hasAvailability = Object.values(
              cart.eligibilities?.serviceCatalog?.categories.find((c) => c.category === Category.SEAT)?.services?.[
                flightOrBound.id
              ] ?? {}
            ).some(({ isAllowedToUse }) => isAllowedToUse);
            const hasControlAvailability =
              cart.controlData.sellAncillariesPerFlight[flightOrBound.flightNumber] || false;

            if (
              (!hasAvailability && !hasControlAvailability) ||
              this.bookingService.cartHasCorporateSeatBenefit(cart, flightOrBound.id)
            ) {
              this.store$.dispatch(setNoAvailability({ flightId: flightOrBound.id }));
              return;
            }
            this.bookingQueueService.queueTriggerFetchSeatMap(
              flightOrBound.id,
              cart.prices.unpaid.total.total.totalAmount.currencyCode
            );
          })
      );
    }
  }

  closeSidebar(): void {
    this.sideBarOpen = false;
    this._showCtaButtonSubject$.next(true);
  }

  closeConfirmModal(confirmData?: ConfirmModalData): void {
    this.confirmModalData$.next({
      open: false,
      confirmData: {
        variant: confirmData?.variant,
        flightOrBoundId: confirmData?.flightOrBoundId,
        travelerId: confirmData?.travelerId,
        removeSelection: confirmData?.removeSelection,
      },
    });
  }

  setConfirmModalData(
    confirmData: ConfirmModalData,
    confirmationModalTranslationKeys: ConfirmModalTranslationKeys
  ): void {
    this.confirmModalData$.next({
      open: true,
      confirmData: {
        ...confirmData,
        confirmationModalTranslationKeys: confirmationModalTranslationKeys,
      },
    });
  }

  private sendEcommerceAddRemoveEvents(
    category: Category,
    cartBeforeUpdate: FinnairCart,
    cartAfterUpdate: FinnairCart,
    purchaseFlow: GtmPurchaseFlow
  ): void {
    const { addedServices, removedServices, addedProducts, removedProducts } = changedServices(
      cartBeforeUpdate,
      cartAfterUpdate,
      category,
      purchaseFlow
    );

    if (addedServices.length) {
      this.gtmService.ecommerceEvent(
        GtmEvent.ADD_SERVICES,
        GtmEcommerceMeasurement.ADD,
        addedServices,
        purchaseFlow,
        cartAfterUpdate?.prices.unpaid.total.total.totalAmount.currencyCode
      );
    }

    if (addedProducts.length) {
      this.gtmService.ecommerceEventGA4(GtmEvent.ADD_TO_CART, addedProducts);
    }

    if (removedServices.length) {
      this.gtmService.ecommerceEvent(
        GtmEvent.REMOVE_SERVICES,
        GtmEcommerceMeasurement.REMOVE,
        removedServices,
        purchaseFlow
      );
    }

    if (removedProducts.length) {
      this.gtmService.ecommerceEventGA4(GtmEvent.REMOVE_FROM_CART, removedProducts);
    }
  }

  changeSeats({ travelerId, flightId, seatNumber }: SeatChangeEvent): void {
    this.seatSelectionLoading$.next(true);
    this.subscriptions.add(
      this.flights$
        .pipe(
          take(1),
          map((flights) => {
            return flights.find((f) => f.id === flightId);
          }),
          switchMap((flight) => {
            return combineLatest([flight.seatMap$, this.store$.pipe(cartStatus())]).pipe(
              filter(([_, cs]) => cs === CartStatus.OK),
              map(([seatMap]) => seatMap)
            );
          })
        )
        .subscribe((seatMap) => {
          const selectedSeat = seatMap.seats.find((seat) => seat.seatNumber === seatNumber);
          this.store$.dispatch(
            SeatMapActions.setSeatForFlight({
              flightId,
              travelerId,
              seat: selectedSeat,
            })
          );
          this.seatSelectionLoading$.next(false);
        })
    );
  }

  // Something of a placeholder for now, but here to be easily extended with more fine-tuned conditions
  private getUpsellCategory(cart: FinnairCart): Category {
    return [Category.SEAT, Category.CABIN_BAGGAGE, Category.BAGGAGE].find((category) =>
      cart.bounds.some((bound) => passengersMissingCategoryService(category, cart, bound))
    );
  }

  private resolveProductCardNotification(
    category: Category,
    count: number,
    noQuotaLeft: boolean,
    hasCorporateSeatBenefit: boolean
  ): ProductCardNotification {
    const notification = { theme: NotificationTheme.INFO };
    if (noQuotaLeft && count === 0) {
      return {
        messageKey: `services.${category}.noQuotaLeft`,
        theme: NotificationTheme.WARNING,
      };
    }
    if (hasCorporateSeatBenefit) {
      return {
        ...notification,
        messageKey: 'services.seat.corporateBenefit',
        icon: SvgLibraryIcon.B2B,
        titleKey: 'services.seat.corporateBenefitTitle',
      };
    }
    // TODO: this should also check from store that theres bundles available for some pax & flight.
    if (this.configService.cfg.enableBookingFlowAncillaryBundles && category === Category.SEAT) {
      return {
        theme: NotificationTheme.CAMPAIGN,
        messageKey: 'services.seat.bundleNotification',
        icon: SvgLibraryIcon.DISCOUNT,
      };
    }
    return notification;
  }
}
